import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, FormControlLabel, Checkbox, Typography, MenuItem } from '@mui/material';
import Paper from '@mui/material/Paper';

export default function StepOtherHistory({ formData, onChange, fieldErrors = {} }) {
  const { t } = useTranslation('otherHistory');
  
  // Helper to handle checkbox groups
  const handleCheckbox = (field, value) => {
    onChange({ [field]: value });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom color="primary">
            {t('sections.aboutSection')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('sections.aboutDescription1')}
          </Typography>
          <Typography variant="body1">
            {t('sections.aboutDescription2')}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">{t('sections.familyHistory')}</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.fh_breast_cancer === 'Yes'} onChange={e => handleCheckbox('fh_breast_cancer', e.target.checked ? 'Yes' : 'No')} />}
              label={t('labels.breastCancer')}
            />
          </Grid>
          {formData.fh_breast_cancer === 'Yes' && (
            <Grid item xs={12} sm={9}>
              <TextField
                label={t('labels.breastCancerDetails')}
                fullWidth
                value={formData.fh_breast_cancer_details || ''}
                onChange={e => onChange({ fh_breast_cancer_details: e.target.value })}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.fh_other_cancer === 'Yes'} onChange={e => handleCheckbox('fh_other_cancer', e.target.checked ? 'Yes' : 'No')} />}
              label={t('labels.otherCancers')}
            />
          </Grid>
          {formData.fh_other_cancer === 'Yes' && (
            <Grid item xs={12} sm={9}>
              <TextField
                label={t('labels.otherCancersDetails')}
                fullWidth
                value={formData.fh_other_cancer_details || ''}
                onChange={e => onChange({ fh_other_cancer_details: e.target.value })}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.fh_blood_clots === 'Yes'} onChange={e => handleCheckbox('fh_blood_clots', e.target.checked ? 'Yes' : 'No')} />}
              label={t('labels.bloodClots')}
            />
          </Grid>
          {formData.fh_blood_clots === 'Yes' && (
            <Grid item xs={12} sm={9}>
              <TextField
                label={t('labels.bloodClotsDetails')}
                fullWidth
                value={formData.fh_blood_clots_details || ''}
                onChange={e => onChange({ fh_blood_clots_details: e.target.value })}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.fh_bleeding_disorders === 'Yes'} onChange={e => handleCheckbox('fh_bleeding_disorders', e.target.checked ? 'Yes' : 'No')} />}
              label={t('labels.bleedingDisorders')}
            />
          </Grid>
          {formData.fh_bleeding_disorders === 'Yes' && (
            <Grid item xs={12} sm={9}>
              <TextField
                label={t('labels.bleedingDisordersDetails')}
                fullWidth
                value={formData.fh_bleeding_disorders_details || ''}
                onChange={e => onChange({ fh_bleeding_disorders_details: e.target.value })}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('sections.socialHistory')}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          label={t('labels.maritalStatus')}
          fullWidth
          value={formData.marital_status || ''}
          onChange={e => onChange({ marital_status: e.target.value })}
        >
          <MenuItem value="Single">{t('options.maritalStatus.single')}</MenuItem>
          <MenuItem value="Married">{t('options.maritalStatus.married')}</MenuItem>
          <MenuItem value="Divorce/Widowed">{t('options.maritalStatus.divorcedWidowed')}</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.children')}
          type="number"
          fullWidth
          value={formData.children || ''}
          onChange={e => onChange({ children: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          label={t('labels.handedness')}
          fullWidth
          value={formData.handedness || ''}
          onChange={e => onChange({ handedness: e.target.value })}
        >
          <MenuItem value="Right handed">{t('options.handedness.right')}</MenuItem>
          <MenuItem value="Left handed">{t('options.handedness.left')}</MenuItem>
          <MenuItem value="Ambidextrous">{t('options.handedness.ambidextrous')}</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.occupation')}
          fullWidth
          value={formData.occupation || ''}
          onChange={e => onChange({ occupation: e.target.value })}
        />
      </Grid>
      
      <Grid item xs={12} sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>{t('sections.socialHabits')}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              label={t('labels.smoking')}
              fullWidth
              value={formData.smoking || ''}
              onChange={e => onChange({ smoking: e.target.value })}
            >
              <MenuItem value="Yes">{t('options.smoking.yes')}</MenuItem>
              <MenuItem value="No">{t('options.smoking.no')}</MenuItem>
              <MenuItem value="Never">{t('options.smoking.never')}</MenuItem>
            </TextField>
          </Grid>
          {formData.smoking === 'Yes' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('labels.smokingHowMuch')}
                fullWidth
                placeholder={t('placeholders.smokingAmount')}
                value={formData.smoking_how_much || ''}
                onChange={e => onChange({ smoking_how_much: e.target.value })}
              />
            </Grid>
          )}
          {formData.smoking === 'No' && (
            <Grid item xs={6} sm={3}>
              <TextField
                label={t('labels.smokingQuitYear')}
                fullWidth
                value={formData.smoking_quit_date || ''}
                onChange={e => onChange({ smoking_quit_date: e.target.value })}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              label={t('labels.alcohol')}
              fullWidth
              value={formData.alcohol || ''}
              onChange={e => onChange({ alcohol: e.target.value })}
            >
              <MenuItem value="Yes">{t('options.yesNo.yes')}</MenuItem>
              <MenuItem value="No">{t('options.yesNo.no')}</MenuItem>
            </TextField>
          </Grid>
          {formData.alcohol === 'Yes' && (
            <Grid item xs={6} sm={6}>
              <TextField
                label={t('labels.alcoholHowOften')}
                fullWidth
                placeholder={t('placeholders.alcoholFrequency')}
                value={formData.alcohol_how_often || ''}
                onChange={e => onChange({ alcohol_how_often: e.target.value })}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={8} sm={5}>
            <TextField
              select
              label={t('labels.drugs')}
              fullWidth
              value={formData.drugs || ''}
              onChange={e => onChange({ drugs: e.target.value })}
            >
              <MenuItem value="Yes">{t('options.yesNo.yes')}</MenuItem>
              <MenuItem value="No">{t('options.yesNo.no')}</MenuItem>
            </TextField>
          </Grid>
          {formData.drugs === 'Yes' && (
            <Grid item xs={6} sm={6}>
              <TextField
                label={t('labels.drugsExplain')}
                fullWidth
                value={formData.drugs_explain || ''}
                onChange={e => onChange({ drugs_explain: e.target.value })}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      
      <Grid item xs={12} sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>{t('sections.heightWeight')}</Typography>
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label={t('labels.heightFeet')}
          type="number"
          fullWidth
          value={formData.height_feet || ''}
          onChange={e => {
            const feet = e.target.value;
            const inches = formData.height_inches || 0;
            const weight = formData.weight || 0;
            let bmi = '';
            if (feet && inches && weight) {
              const totalInches = parseInt(feet) * 12 + parseInt(inches);
              bmi = ((parseFloat(weight) / (totalInches * totalInches)) * 703).toFixed(1);
            }
            onChange({ height_feet: feet, bmi });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label={t('labels.heightInches')}
          type="number"
          fullWidth
          value={formData.height_inches || ''}
          onChange={e => {
            const inches = e.target.value;
            const feet = formData.height_feet || 0;
            const weight = formData.weight || 0;
            let bmi = '';
            if (feet && inches && weight) {
              const totalInches = parseInt(feet) * 12 + parseInt(inches);
              bmi = ((parseFloat(weight) / (totalInches * totalInches)) * 703).toFixed(1);
            }
            onChange({ height_inches: inches, bmi });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label={t('labels.weight')}
          type="number"
          fullWidth
          value={formData.weight || ''}
          onChange={e => {
            const weight = e.target.value;
            const feet = formData.height_feet || 0;
            const inches = formData.height_inches || 0;
            let bmi = '';
            if (feet && inches && weight) {
              const totalInches = parseInt(feet) * 12 + parseInt(inches);
              bmi = ((parseFloat(weight) / (totalInches * totalInches)) * 703).toFixed(1);
            }
            onChange({ weight, bmi });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label={t('labels.bmi')}
          fullWidth
          value={formData.bmi || ''}
          InputProps={{ 
            readOnly: true,
            style: { color: '#9e9e9e' } 
          }}
          InputLabelProps={{ style: { color: '#9e9e9e' } }}
          helperText={t('labels.bmiCalculated')}
          FormHelperTextProps={{ style: { color: '#9e9e9e' } }}
        />
      </Grid>

      {/* Emergency Contact Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>{t('sections.emergencyContact')}</Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label={t('labels.contactName')}
          fullWidth
          value={formData.emergency_contact_name || ''}
          onChange={e => onChange({ emergency_contact_name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label={t('labels.relationship')}
          fullWidth
          value={formData.emergency_contact_relationship || ''}
          onChange={e => onChange({ emergency_contact_relationship: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label={t('labels.contactPhone')}
          fullWidth
          value={
            // Format phone number as ###-###-#### if it has enough digits
            formData.emergency_contact_phone ? 
              formData.emergency_contact_phone
                .replace(/^(\d{3})(\d{0,3})(\d{0,4})$/, (match, p1, p2, p3) => {
                  let formatted = p1;
                  if (p2) formatted += '-' + p2;
                  if (p3) formatted += '-' + p3;
                  return formatted;
                })
              : ''
          }
          onChange={e => {
            // Only allow digits for storage
            const cleaned = e.target.value.replace(/\D/g, '');
            onChange({ emergency_contact_phone: cleaned });
          }}
          inputProps={{ 
            maxLength: 12, // Increased to account for the two hyphens
            inputMode: 'tel'
          }}
          error={formData.emergency_contact_phone && formData.emergency_contact_phone.length !== 10}
          helperText={formData.emergency_contact_phone && formData.emergency_contact_phone.length !== 10 ? t('errors.validPhone') : ''}
        />
      </Grid>
    </Grid>
  );
}
